import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMemo, useState } from "react";
import ResourcesProductionInfoDialog from "./ResourcesProductionInfoDialog";
import {
  formatJurisdictionLabel,
  formatNumberWithCommas,
  formatPercent,
} from "../../utils/companiesFormatting";
import { getCommodityMeta } from "./functions";
import AssetResourceDetailDrawer from "./AssetResourceDetailDrawer";
import {
  PRODUCTION_METRIC_CATEGORIES,
  PRODUCTION_METRIC_CODE_ORDER,
  PRODUCTION_METRIC_ORDER,
} from "./productionUtils";

export default function ForecastsTab({
  annualData: providedAnnualData,
  annualLoading: providedAnnualLoading,
  annualError: providedAnnualError,
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [detailDrawer, setDetailDrawer] = useState({ open: false, type: "", data: null });
  const resolvedAnnualData = providedAnnualData ?? null;
  const loading = providedAnnualLoading ?? false;
  const error = providedAnnualError ?? null;

  const data = resolvedAnnualData?.forecast || null;
  const periods = useMemo(() => {
    const items = data?.periods || [];
    return [...items].sort((a, b) => {
      const aTime = a?.end_date ? new Date(a.end_date).getTime() : 0;
      const bTime = b?.end_date ? new Date(b.end_date).getTime() : 0;
      return aTime - bTime;
    });
  }, [data]);
  const periodKeys = useMemo(() => periods.map((period) => period.key), [periods]);
  const companyMetrics = useMemo(() => data?.company_summary?.metrics || [], [data]);
  const assetDetail = useMemo(() => data?.asset_detail || [], [data]);

  const allExpanded = useMemo(() => {
    if (!assetDetail.length) return false;
    return assetDetail.every((asset) => expandedAssets[asset.asset_id]);
  }, [assetDetail, expandedAssets]);

  const toggleAssetExpanded = (assetId) => {
    setExpandedAssets((prev) => ({ ...prev, [assetId]: !prev[assetId] }));
  };
  const expandAllAssets = () => {
    const next = {};
    assetDetail.forEach((asset) => {
      if (asset?.asset_id != null) {
        next[asset.asset_id] = true;
      }
    });
    setExpandedAssets(next);
  };
  const collapseAllAssets = () => {
    setExpandedAssets({});
  };
  const openDetailDrawer = (type, data) => {
    setDetailDrawer({ open: true, type, data });
  };
  const closeDetailDrawer = () => {
    setDetailDrawer({ open: false, type: "", data: null });
  };

  const buildMetricRows = (metrics = []) => {
    const rows = metrics.map((metric) => {
      const unitLabel = metric.unit_display || metric.unit;
      const baseLabel = metric.label || metric.metric || metric.metric_code || "Metric";
      const label = unitLabel ? `${baseLabel} (${unitLabel})` : baseLabel;
      const code = metric.metric_code || metric.metric || "";
      return {
        type: "metric",
        key: metric.metric_key || `${code}-${metric.commodity || "company"}-${label}`,
        label,
        code,
        values: metric.values || {},
      };
    });
    const forecastOrder = {
      ounces_produced: 0,
      aisc: 1,
    };
    const order = (code) =>
      forecastOrder[code] ??
      PRODUCTION_METRIC_CODE_ORDER[code] ??
      Number.MAX_SAFE_INTEGER;
    rows.sort((a, b) => {
      const aOrder = order(a.code);
      const bOrder = order(b.code);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.label.localeCompare(b.label);
    });
    return rows;
  };

  const formatMetricValue = (metric, key) => {
    const value = metric.values?.[key];
    if (value === undefined || value === null) return "—";
    const isAisc = metric.code === "aisc";
    return formatNumberWithCommas(value, isAisc ? 0 : null);
  };

  const renderForecastTable = (metrics, tablePeriods, tablePeriodKeys, report) => {
    if (!metrics.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No published production forecasts available.
        </Typography>
      );
    }
    return (
      <Box sx={{ width: "100%" }}>
        {report && (
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                As of date:
              </Box>{" "}
              {report.as_of_date || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Forecast name:
              </Box>{" "}
              {report.source_title || "Report"}
            </Typography>
            {report.source_url && (
              <Typography variant="caption" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Report URL:
                </Box>{" "}
                {report.source_url}
              </Typography>
            )}
          </Stack>
        )}
        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 1.5,
            overflowX: { xs: "scroll", md: "hidden" },
            WebkitOverflowScrolling: "touch",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `1.6fr repeat(${tablePeriods.length}, 1fr)`,
              gap: 1,
              px: 2,
              py: 0.75,
              backgroundColor: "rgba(34, 35, 37, 0.04)",
            }}
          >
            <Typography variant="caption" fontWeight={700}>
              Metric
            </Typography>
            {tablePeriods.map((period) => (
              <Typography key={period.key} variant="caption" fontWeight={700}>
                {period.label}
              </Typography>
            ))}
          </Box>
          <Stack spacing={0} sx={{ px: 2, py: 0.75 }}>
            {metrics.map((metric) => {
              const unitLabel = metric.unit_display || metric.unit;
              const metricName = metric.metric || metric.label;
              const metricLabel = unitLabel ? `${metricName} (${unitLabel})` : metricName;
              const isAisc = (metric.metric_code || metric.metric || "") === "aisc";
              const commodityKey = metric.commodity || metric.commodity_code;
              const meta = commodityKey ? getCommodityMeta(commodityKey) : null;
              const chipLabel = meta?.symbol || commodityKey;
              return (
                <Box
                  key={metric.metric_key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `1.6fr repeat(${tablePeriods.length}, 1fr)`,
                    gap: 1,
                    alignItems: "center",
                    py: 0.6,
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {commodityKey ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        <Chip
                          label={chipLabel}
                          size="small"
                          sx={{
                            backgroundColor: meta?.color || "action.hover",
                            color: "#0f172a",
                            fontWeight: 700,
                            height: 22,
                          }}
                          title={meta?.label || commodityKey}
                        />
                        <Box component="span">{metricLabel}</Box>
                      </Box>
                    ) : (
                      metricLabel
                    )}
                  </Typography>
                  {tablePeriodKeys.map((key) => (
                    <Typography
                      key={`${metric.metric_key}-${key}`}
                      variant="caption"
                      color="text.secondary"
                    >
                      {metric.values?.[key] !== undefined
                        ? formatNumberWithCommas(metric.values[key], isAisc ? 0 : null)
                        : "—"}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    );
  };

  const hasForecastData = companyMetrics.length > 0 || assetDetail.length > 0;

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Forecasts
        </Typography>
        {!loading && !error && hasForecastData && (
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            aria-label="Forecasts information"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <ResourcesProductionInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      {loading ? (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Skeleton height={96} />
        </Box>
      ) : error ? (
        <Alert severity="error">Unable to load forecast data.</Alert>
      ) : !hasForecastData ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No published production forecasts available.
          </Typography>
          <Divider sx={{ pt: 2, mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            <b>ResourceNAV</b> is currently working with companies to build out this asset database to
            create a rich dataset of resources, production and forecast metrics to use for enhanced display & screening
            purposes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you are a mining company, claim your company profile and enter in asset
            information to publish data on the platform.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Company Forecast
            </Typography>
            {companyMetrics.length ? (
              renderForecastTable(companyMetrics, periods, periodKeys, {
                as_of_date: data?.as_of || null,
                source_title: data?.forecast_name || "Company Production Forecast",
                source_url: data?.source_url || "",
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No published production forecasts available.
              </Typography>
            )}
          </Box>

          <Divider />

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>
                Forecast Asset Detail
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={allExpanded ? collapseAllAssets : expandAllAssets}
                sx={{ fontWeight: 600 }}
              >
                {allExpanded ? "Collapse all" : "Expand all"}
              </Button>
            </Stack>
            {assetDetail.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No published asset-level forecast data.
              </Typography>
            ) : (
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1.5,
                  overflowX: "auto",
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse"
                  }}
                >
                  <Box component="thead">
                    <Box component="tr" sx={{ backgroundColor: "rgba(34, 35, 37, 0.04)" }}>
                      <Box
                        component="th"
                        sx={{
                          px: 0.5,
                          py: 0.75,
                          textAlign: "left",
                          width: { xs: 125, sm: 300 },
                          minWidth: { xs: 125, sm: 300 },
                          maxWidth: { xs: 125, sm: 300 },
                          borderRight: "1px solid rgba(0,0,0,0.08)",
                        }}
                      />
                      <Box
                        component="th"
                        rowSpan={2}
                        sx={{
                          px: 0.5,
                          py: 0.75,
                          textAlign: "left",
                          borderRight: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          Metric
                        </Typography>
                      </Box>
                      {periods.map((period) => (
                        <Box
                          key={period.key}
                          component="th"
                          rowSpan={2}
                          sx={{ px: 0.5, py: 0.75, textAlign: "center" }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            {period.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {assetDetail.flatMap((asset) => {
                      const assetId = asset.asset_id;
                      const isExpanded = Boolean(expandedAssets[assetId]);
                      const assetRows = buildMetricRows(asset.metrics || []);
                      const assetLabel = (
                        <Stack direction="column" spacing={0.25} alignItems="flex-start">
                          <Stack direction="row" spacing={0} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleAssetExpanded(assetId)}
                              sx={{ mt: 0, width: 24, height: 24 }}
                              aria-label="Toggle asset resources"
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 20,
                                  height: 20,
                                  fontSize: "0.75rem",
                                  lineHeight: 1,
                                  color: "text.secondary",
                                }}
                              >
                                {isExpanded ? "▼" : "▶"}
                              </Box>
                            </IconButton>
                            <Tooltip title={asset.asset_name} placement="top" arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                  maxWidth: { xs: 125, sm: 220 },
                                  whiteSpace: { xs: "normal", sm: "nowrap" },
                                  overflow: { xs: "visible", sm: "hidden" },
                                  textOverflow: { xs: "clip", sm: "ellipsis" },
                                  display: "block",
                                }}
                                onClick={() => openDetailDrawer("asset", asset)}
                              >
                                {asset.asset_name}
                              </Typography>
                            </Tooltip>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", pl: 3 }}>
                            {[
                              formatJurisdictionLabel(asset.jurisdiction),
                              asset.lifecycle || asset.lifecycle_stage,
                              formatPercent(asset.ownership_percentage, 2) || "—",
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </Typography>
                        </Stack>
                      );

                      const assetRowsRendered = (assetRows.length ? assetRows : [null]).map(
                        (metric, index) => (
                          <Box
                            component="tr"
                            key={`asset-${assetId}-${index}`}
                            sx={{
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                              backgroundColor: "rgba(34, 35, 37, 0.02)",
                            }}
                          >
                            <Box
                              component="td"
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                verticalAlign: "top",
                                width: { xs: 125, sm: 300 },
                                minWidth: { xs: 125, sm: 300 },
                                maxWidth: { xs: 125, sm: 300 },
                                borderRight: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {index === 0 ? assetLabel : null}
                            </Box>
                            <Box component="td" sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                              {metric ? (
                                metric.type === "category" ? (
                                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    {metric.label}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" fontWeight={600}>{metric.label}</Typography>
                                )
                              ) : (
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
                                  —
                                </Typography>
                              )}
                            </Box>
                            {periodKeys.map((key) => (
                              <Box key={`${assetId}-${index}-${key}`} component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: metric ? "text.primary" : "text.disabled" }}>
                                  {metric ? (metric.type === "category" ? "" : formatMetricValue(metric, key)) : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )
                      );

                      if (!isExpanded) {
                        return assetRowsRendered;
                      }

                      const resourceRowsRendered = (asset.resource_detail || []).flatMap((resource) => {
                        const resourceRows = buildMetricRows(resource.metrics || []);
                        const resourceLabel = (
                          <Stack direction="column" spacing={0.25} alignItems="flex-start">
                            <Tooltip title={resource.resource_reserve_name || "Resource reserve"} placement="top" arrow>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                  maxWidth: { xs: 125, sm: 220 },
                                  whiteSpace: { xs: "normal", sm: "nowrap" },
                                  overflow: { xs: "visible", sm: "hidden" },
                                  textOverflow: { xs: "clip", sm: "ellipsis" },
                                  display: "block",
                                  pl: 4,
                                }}
                                onClick={() => openDetailDrawer("resource", resource)}
                              >
                                {resource.resource_reserve_name || "Resource reserve"}
                              </Typography>
                            </Tooltip>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", pl: 4 }}
                            >
                              {resource.lifecycle || "—"}
                            </Typography>
                          </Stack>
                        );
                        return (resourceRows.length ? resourceRows : [null]).map((metric, index) => (
                          <Box
                            component="tr"
                            key={`resource-${assetId}-${resource.resource_reserve_id}-${index}`}
                            sx={{
                              borderTop: "1px solid rgba(0,0,0,0.06)",
                              backgroundColor: "transparent",
                            }}
                          >
                            <Box
                              component="td"
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                verticalAlign: "top",
                                width: { xs: 125, sm: 300 },
                                minWidth: { xs: 125, sm: 300 },
                                maxWidth: { xs: 125, sm: 300 },
                                borderRight: "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              {index === 0 ? resourceLabel : null}
                            </Box>
                            <Box component="td" sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                              {metric ? (
                                metric.type === "category" ? (
                                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    {metric.label}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" fontWeight={600}>{metric.label}</Typography>
                                )
                              ) : (
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}>
                                  —
                                </Typography>
                              )}
                            </Box>
                            {periodKeys.map((key) => (
                              <Box key={`${assetId}-${resource.resource_reserve_id}-${index}-${key}`} component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: metric ? "text.primary" : "text.disabled" }}>
                                  {metric ? (metric.type === "category" ? "" : formatMetricValue(metric, key)) : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ));
                      });

                      return [...assetRowsRendered, ...resourceRowsRendered];
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </Stack>
      )}
      <AssetResourceDetailDrawer
        open={detailDrawer.open}
        onClose={closeDetailDrawer}
        type={detailDrawer.type}
        data={detailDrawer.data}
        showResourceReportInfo={false}
      />
    </Box>
  );
}
